import * as React from 'react';
import Image from 'next/image';
import { formatISK } from '@/utils/utils';
import ImagePlaceholder from '../common/ImagePlaceholder';
import { AnimatePresence, motion } from 'framer-motion';
import { ImageAsset } from '@/models/ProductTypes';

type ProductCardProps = {
  name: string;
  category: string;
  group: string;
  price: number;
  oldPrice?: number;
  available: boolean;
  images?: Array<ImageAsset>;
  showDynamicDiscountLabel?: boolean;
  created?: string;
  hideNewSticker?: boolean;
  energySticker?: string | null;
  discountRange?: string | null; // e.g., "10-30%" or "25%" or null
};

const variants = {
  initial: (custom: string) => ({
    x: custom === 'increase' ? 80 : -80,
    opacity: 0,
    scale: 0.6,
  }),
  animate: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (custom: string) => ({
    x: custom === 'increase' ? -80 : 80,
    opacity: 0,
    scale: 0.6,
  }),
};

/* --------------------------------- Card ------------------------------------ */
const ProductCard: React.FC<ProductCardProps> = ({
  name,
  group,
  price,
  oldPrice,
  available,
  images,
  showDynamicDiscountLabel,
  created,
  hideNewSticker,
  energySticker,
  discountRange,
}) => {
  const [idx, setIdx] = React.useState(0);
  const [direction, setDirection] = React.useState<string>('');
  const [isDragging, setIsDragging] = React.useState(false);
  const [hasLoadedAllImages, setHasLoadedAllImages] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(true);
  const [showShimmer, setShowShimmer] = React.useState(true);

  const hasImages = Array.isArray(images) && images.length > 0;
  const canStep = hasImages && images.length > 1;

  // Keep idx in range if images change
  React.useEffect(() => {
    if (!hasImages || !images) return;
    if (idx > images.length - 1) setIdx(0);
  }, [hasImages, images, idx]);

  // Preload all images on hover
  const loadAllimages = React.useCallback(() => {
    if (!hasImages) return;

    if (hasLoadedAllImages) return;

    images.forEach((image) => {
      const img = new window.Image();
      img.loading = 'eager';
      // Use Next.js image optimization URL format to match what the Image component uses
      const nextImageUrl = `/_next/image?url=${encodeURIComponent(`${image.url}?w=500&q=75`)}&w=384&q=75`;
      img.src = nextImageUrl;
    });

    setHasLoadedAllImages(true);
  }, [hasImages, hasLoadedAllImages, images]);

  const active = hasImages ? images[Math.min(idx, images.length - 1)] : null;

  // Show shimmer only if loading takes longer than 500ms
  React.useEffect(() => {
    if (imageLoading) {
      const shimmerTimer = setTimeout(() => {
        if (imageLoading) {
          setShowShimmer(true);
        }
      }, 200);
      return () => clearTimeout(shimmerTimer);
    }
    return undefined;
  }, [imageLoading]);

  // Handle shimmer completion when image loads
  React.useEffect(() => {
    if (!imageLoading) {
      // Immediately hide shimmer when image loads
      setShowShimmer(false);
    }
  }, [imageLoading]);
  const threeMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString();

  return (
    <div
      className="relative flex h-[500px] w-full flex-col gap-24 overflow-hidden rounded-2xl bg-white-50 shadow-md transition-all duration-200 hover:shadow-lg"
      onMouseEnter={() => {
        if (!hasLoadedAllImages) {
          loadAllimages();
        }
      }}
      onClick={(e) => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {/* Energy Sticker */}
      {energySticker && energySticker !== 'none' && (
        <div className="h-60 w-60 absolute right-0 top-[32px] z-10">
          <Image
            src={`/energyStickers/${energySticker}.png`}
            alt="Orkumerking"
            width={60}
            height={60}
            className="h-auto w-auto"
            draggable={false}
          />
        </div>
      )}

      {/* Image area */}
      <div className="relative h-[260px] w-full select-none bg-white-100 p-24">
        <div className="relative h-0">
          {created !== undefined && created > threeMonthsAgo && !hideNewSticker && (
            <div className="absolute -left-16 -top-16 z-10 flex h-80 w-80 flex-col items-center justify-center rounded-full bg-red-100 text-[14px] tracking-[0.1px] text-white-100">
              <span className="text-lg font-bold leading-tight">Nýtt</span>
            </div>
          )}
        </div>

        {images === null && <div className="bg-gray-100 h-full w-full animate-pulse" />}

        {active && (
          <>
            <div className="relative h-full w-full">
              {/* key ensures full swap so you *see* the change */}
              <AnimatePresence mode="popLayout" initial={false} custom={direction}>
                <motion.div
                  key={active.url || `img-${idx}`}
                  custom={direction}
                  variants={variants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="relative h-full w-full touch-pan-y"
                  style={{
                    cursor: canStep ? (isDragging ? 'grabbing' : 'pointer') : 'default',
                    touchAction: canStep ? 'pan-y' : 'auto',
                  }}
                  drag={canStep ? 'x' : false}
                  dragConstraints={{ left: 5, right: 5 }}
                  dragElastic={0.1}
                  dragMomentum={false}
                  dragPropagation={false}
                  whileDrag={{ scale: 1.02, cursor: 'grabbing' }}
                  onPointerDown={(e) => {
                    if (canStep) {
                      e.preventDefault();
                      setIsDragging(false);
                    }
                  }}
                  onDragStart={(e) => {
                    document.body.style.userSelect = 'none';
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragEnd={(_, info) => {
                    document.body.style.userSelect = '';

                    // Small delay to ensure click events can be properly prevented
                    setTimeout(() => setIsDragging(false), 100);

                    if (!canStep) return;

                    const swipeThreshold = 50;
                    const swipeVelocityThreshold = 300;

                    if (info.offset.x > swipeThreshold || info.velocity.x > swipeVelocityThreshold) {
                      const newIdx = idx > 0 ? idx - 1 : (images?.length ?? 1) - 1;
                      setDirection('decrease');
                      setIdx(newIdx);
                    } else if (info.offset.x < -swipeThreshold || info.velocity.x < -swipeVelocityThreshold) {
                      const newIdx = idx < (images?.length ?? 1) - 1 ? idx + 1 : 0;
                      setDirection('increase');
                      setIdx(newIdx);
                    }
                  }}
                  onClick={(e) => {
                    if (isDragging) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: imageLoading ? 0 : 1 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="absolute inset-0"
                  >
                    <Image
                      key={active.url || `img-${idx}`}
                      alt={active.title || name || 'vara'}
                      src={`${active.url}?w=500&q=75&fm=webp`}
                      fill
                      className="p-6 pointer-events-none object-contain"
                      sizes="(max-width: 640px) 100vw, 25vw"
                      priority={false}
                      draggable={false}
                      onLoad={() => {
                        setImageLoading(false);
                      }}
                    />
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              {canStep && (
                <div
                  className="pointer-events-auto absolute -bottom-16 left-0 right-0 z-10 flex justify-center gap-8"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {images?.map((_, i) => (
                    <motion.button
                      layout
                      transition={{
                        ease: 'easeInOut',
                        layout: { duration: 0.2 },
                      }}
                      key={i}
                      type="button"
                      aria-label={`Fara á mynd ${i + 1}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDirection(i > idx ? 'increase' : 'decrease');
                        setIdx(i);
                      }}
                      className={`h-12 rounded-full border border-white-5 ${
                        i === idx ? 'w-16 bg-black-75' : 'w-12 bg-black-25'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            <AnimatePresence>
              {showShimmer && (
                <motion.div
                  className="bg-gray-100 absolute inset-0 animate-pulse overflow-hidden"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    key={`shimmer-${idx}`}
                    className="absolute -left-1/2 -top-1/2 h-[200%] w-[200%]"
                    style={{
                      background: 'linear-gradient(100deg, transparent 40%, #f0f0f1 50%, transparent 60%)',
                    }}
                    initial={{
                      x: '-30%',
                      y: '-30%',
                    }}
                    animate={{
                      x: ['-30%', '80%'],
                      y: ['-30%', '80%'],
                    }}
                    transition={{
                      duration: 1.5,
                      ease: 'easeOut',
                      repeat: imageLoading ? Infinity : 0,
                      repeatType: 'loop',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {images && images.length === 0 && <ImagePlaceholder />}
        <div className="relative h-0">
          {!available && (
            <div className="absolute -left-24 bottom-[-36px] w-fit rounded-r-8 bg-primary-light-green px-24 py-8 text-[14px] font-bold tracking-[0.1px] text-primary-deep-sea">
              <span>Uppselt</span>
            </div>
          )}
        </div>
        <div className="relative h-0">
          {(showDynamicDiscountLabel === undefined || showDynamicDiscountLabel) &&
            (discountRange || !!oldPrice) &&
            (() => {
              const displayText =
                discountRange || (oldPrice ? `${Math.floor(((oldPrice - price) / oldPrice) * 100)}%` : '');
              const hasAlltAd = displayText.includes('Allt að');

              return (
                <div
                  className={`absolute -bottom-48 -right-40 flex flex-col items-center justify-center rounded-full bg-primary-sea text-white-100 ${
                    hasAlltAd ? 'px-3 py-3 h-[115px] w-[115px]' : 'h-100 w-100'
                  }`}
                >
                  <span className={`text-center leading-tight ${hasAlltAd ? 'text-[13px]' : 'text-lg font-bold'}`}>
                    {hasAlltAd ? (
                      <>
                        <span className="font-normal">Allt að </span>
                        <span className="font-bold">{displayText.replace('Allt að ', '')}</span>
                      </>
                    ) : (
                      displayText
                    )}
                  </span>
                  <span className={`font-light leading-tight ${hasAlltAd ? 'mt-0.5 text-[11px]' : 'text-sm'}`}>
                    Afsláttur
                  </span>
                </div>
              );
            })()}
        </div>
      </div>

      {/* Content */}
      <div className="mb-24 flex flex-1 flex-col justify-between px-24">
        <div>
          <span className="text-xs mt-1 text-[12px] text-neutral-10">{group}</span>
          <h2 className="text-gray-900 line-clamp-2 text-[24px] font-semibold">{name}</h2>
        </div>

        <div className="mt-3 flex flex-col items-start justify-start text-primary-red">
          <span className="text-[12px]">Verð frá:</span>
          <span className="text-lg font-bold">{formatISK(price)}</span>
          <span className={`text-[14px] font-bold line-through ${!!oldPrice ? '' : 'invisible'}`}>
            {formatISK(oldPrice)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
